import { 
  Building2, 
  Cpu, 
  Brain, 
  Zap, 
  Eye, 
  BarChart3, 
  Shield, 
  Smartphone, 
  Settings, 
  Globe, 
  Database, 
  Wifi, 
  Lock, 
  Activity, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Users, 
  Wrench, 
  Thermometer, 
  Battery, 
  Camera, 
  Gauge, 
  MapPin, 
  Radio, 
  Monitor, 
  HardDrive, 
  Cloud, 
  FileText, 
  Target, 
  TrendingUp, 
  Layers, 
  Radar,
  Lightbulb,
  Wind,
  Droplets,
  Flame,
  Home,
  Building,
  Factory,
  Warehouse,
  TreePine,
  Car,
  Truck,
  Bell,
  ClipboardCheck,
  UserCheck,
  Calendar,
  Mail,
  Network,
  Rocket,
  Mic,
  Workflow,
  ScanLine,
  RefreshCw,
  LineChart,
  BatteryCharging,
  ShieldCheck,
  Fingerprint
} from 'lucide-react';
import type { ProductFeatureConfig } from './types';

export const facilitiesManagementSuiteConfig: ProductFeatureConfig = {
  keyBenefits: [
    {
      icon: Building2,
      title: "Interactive 3D Digital Twin Facility Visualization",
      description: "Real-time 3D building models with live data overlays, IoT sensor integration, and immersive facility exploration capabilities for complete spatial awareness.",
      metrics: "Real-time 3D visualization with 100+ data points",
      color: "from-emerald-500 to-teal-600"
    },
    {
      icon: Brain,
      title: "AI Copilot & Autonomous Agents", 
      description: "Intelligent AI that balances autonomous action with decision-making assistance, auto-scheduling maintenance and providing real-time recommendations to facility managers.",
      metrics: "95% task automation accuracy, 24/7 availability",
      color: "from-blue-500 to-cyan-600"
    },
    {
      icon: Cpu,
      title: "Predictive Maintenance Intelligence",
      description: "Advanced ML algorithms forecast equipment failures based on historical trends and real-time sensor data, significantly reducing unplanned downtime and maintenance costs.",
      metrics: "40% reduction in unplanned downtime",
      color: "from-purple-500 to-pink-600"
    },
    {
      icon: Activity,
      title: "Real-Time IoT Data Overlay & Live Monitoring",
      description: "Seamless integration with thousands of IoT sensors providing real-time environmental, occupancy, and equipment data visualization across all facilities.",
      metrics: "1000+ sensors supported, <1 second latency",
      color: "from-green-500 to-emerald-600"
    },
    {
      icon: BarChart3,
      title: "AI-Driven Space Optimization & Occupancy Analytics",
      description: "Intelligent space utilization analysis with occupancy patterns, helping optimize workspace efficiency and reduce real estate costs with data-driven insights.",
      metrics: "25% space efficiency improvement",
      color: "from-orange-500 to-red-600"
    },
    {
      icon: Globe,
      title: "Centralized Multi-Site Facility Management",
      description: "Unified dashboard for managing multiple buildings, campuses, or geographic locations from a single comprehensive enterprise platform.",
      metrics: "Unlimited facilities, global deployment",
      color: "from-indigo-500 to-purple-600"
    },
    {
      icon: ScanLine,
      title: "One-Step Asset Creation with Photo Recognition",
      description: "Simply upload a photo to auto-populate asset details including make, model, and serial number using advanced image recognition and OCR technology.",
      metrics: "90% faster asset onboarding",
      color: "from-teal-500 to-cyan-600"
    },
    {
      icon: Mic,
      title: "Mobile-First Voice-to-Text Operations",
      description: "Hands-free updates for technicians in the field with voice-to-text functionality, enabling real-time reporting and task completion while working.",
      metrics: "98% voice recognition accuracy",
      color: "from-pink-500 to-rose-600"
    },
    {
      icon: RefreshCw,
      title: "Intelligent Resource Allocation Engine",
      description: "Dynamically identifies the most efficient times for maintenance to minimize redundant efforts, peak-hour interruptions, and resource conflicts.",
      metrics: "35% improvement in resource utilization",
      color: "from-amber-500 to-orange-600"
    },
    {
      icon: BatteryCharging,
      title: "Dynamic Energy Optimization",
      description: "Automatically adjusts HVAC and lighting in real-time based on occupancy data, external weather conditions, and energy tariff schedules.",
      metrics: "30% reduction in energy costs",
      color: "from-lime-500 to-green-600"
    },
    {
      icon: Shield,
      title: "Enterprise-Grade Security & Compliance",
      description: "Two-factor authentication, end-to-end encryption, comprehensive audit trails for regulatory compliance, and continuous security monitoring.",
      metrics: "SOC 2, ISO 27001, GDPR compliant",
      color: "from-red-500 to-rose-600"
    },
    {
      icon: Workflow,
      title: "Proactive Operational Model",
      description: "Shift from reactive 'fix-it-when-it-breaks' to an anticipatory approach, safeguarding asset longevity and preventing costly emergency repairs.",
      metrics: "60% reduction in emergency repairs",
      color: "from-violet-500 to-purple-600"
    }
  ],
  
  advancedCapabilities: [
    {
      icon: Eye,
      title: "Augmented Reality (AR) Maintenance Guidance",
      description: "AR-powered maintenance instructions overlaying digital information on real equipment for guided repairs, inspections, and training.",
      metrics: "75% faster repair times with AR guidance",
      color: "from-purple-500 to-indigo-600"
    },
    {
      icon: Layers,
      title: "Virtual 'What-If' Scenario Modeling & Simulation",
      description: "Advanced simulation engine for testing facility changes, emergency scenarios, and optimization strategies before implementation in a risk-free environment.",
      metrics: "Unlimited scenario simulations, risk-free testing",
      color: "from-blue-500 to-purple-600"
    },
    {
      icon: AlertTriangle,
      title: "Digital Twin-Based Emergency Response Planning",
      description: "Emergency evacuation route optimization, real-time crowd management, and automated first responder notification with live building data.",
      metrics: "Sub-second emergency response activation",
      color: "from-red-500 to-orange-600"
    },
    {
      icon: TrendingUp,
      title: "Self-Learning Asset Performance & Risk Scoring",
      description: "Machine learning algorithms that continuously learn from facility data to improve performance predictions and risk assessments over time.",
      metrics: "Continuously improving 95%+ accuracy",
      color: "from-green-500 to-teal-600"
    },
    {
      icon: Wind,
      title: "Smart HVAC & Climate Control Automation",
      description: "Intelligent building automation with occupancy-based climate control, energy optimization, peak demand management, and comfort optimization.",
      metrics: "40% HVAC efficiency improvement",
      color: "from-cyan-500 to-blue-600"
    },
    {
      icon: Monitor,
      title: "Edge Computing for Real-Time Decision Making",
      description: "On-site edge computing capabilities for instant data processing, reduced latency, and autonomous facility operations without cloud dependency.",
      metrics: "<10ms edge processing latency",
      color: "from-gray-500 to-slate-600"
    },
    {
      icon: Wrench,
      title: "Complete Asset Lifecycle Management",
      description: "End-to-end asset tracking from procurement through disposal, including depreciation, warranty management, and maintenance history.",
      metrics: "100% asset visibility across lifecycle",
      color: "from-amber-500 to-yellow-600"
    },
    {
      icon: LineChart,
      title: "Advanced Analytics & Business Intelligence",
      description: "Custom report builder, executive dashboards, predictive analytics, cost analysis, and ROI calculation tools for data-driven decisions.",
      metrics: "Real-time KPI monitoring & forecasting",
      color: "from-indigo-500 to-blue-600"
    },
    {
      icon: ClipboardCheck,
      title: "Automated Compliance & Audit Management",
      description: "UK regulatory compliance automation for GDPR, HSE, Building Regulations with blockchain-based immutable audit trails and automated reporting.",
      metrics: "100% regulatory compliance automation",
      color: "from-emerald-500 to-green-600"
    },
    {
      icon: TreePine,
      title: "Sustainability & Carbon Footprint Tracking",
      description: "Environmental impact assessment, carbon footprint tracking, waste management programs, and green building certification support.",
      metrics: "Complete ESG reporting & tracking",
      color: "from-green-600 to-emerald-600"
    },
    {
      icon: Network,
      title: "Enterprise Integration Hub",
      description: "Seamless API integration with ERP, HR, Finance, and existing business systems with pre-built connectors for SAP, Oracle, and Microsoft.",
      metrics: "50+ pre-built enterprise integrations",
      color: "from-blue-600 to-indigo-600"
    },
    {
      icon: Smartphone,
      title: "Native Mobile Apps for Field Teams",
      description: "iOS and Android apps for technicians with offline capability, barcode/QR scanning, photo documentation, and real-time work order management.",
      metrics: "Full offline capability, instant sync",
      color: "from-pink-500 to-purple-600"
    }
  ],
  
  securityPerformance: [
    {
      icon: Target,
      title: 'Compliance Excellence Framework',
      description: 'Automated compliance monitoring for SOC 2, ISO 27001, GDPR, HSE, and industry-specific regulations with continuous validation',
      metrics: 'Complete regulatory compliance',
      color: 'from-indigo-500 to-purple-600'
    },
    {
      icon: Activity,
      title: 'Security Performance Analytics',
      description: 'Advanced security metrics with threat landscape analysis and security posture optimization dashboards',
      metrics: 'Optimized security posture',
      color: 'from-yellow-500 to-amber-600'
    },
    {
      icon: Rocket,
      title: 'Scalable Security Infrastructure',
      description: 'Enterprise-grade security platform supporting unlimited users with global threat intelligence feeds',
      metrics: 'Unlimited security scale',
      color: 'from-emerald-500 to-green-600'
    },
    {
      icon: Shield,
      title: 'Advanced Firewall & DDoS Protection',
      description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
      metrics: '99.99% attack mitigation',
      color: 'from-red-500 to-pink-600'
    },
    {
      icon: Fingerprint,
      title: 'Multi-Factor Authentication Suite',
      description: 'Enterprise SSO with biometric authentication, hardware tokens, and risk-based access controls',
      metrics: 'Zero-trust access control',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      icon: Eye,
      title: 'Security Information & Event Management',
      description: 'Real-time SIEM with behavioral analytics, threat hunting, and automated incident correlation',
      metrics: 'Real-time threat visibility',
      color: 'from-purple-500 to-pink-600'
    },
    {
      icon: Database,
      title: 'Data Loss Prevention & Backup',
      description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
      metrics: 'Zero data loss guarantee',
      color: 'from-green-500 to-emerald-600'
    },
    {
      icon: Network,
      title: 'Network Segmentation & Monitoring',
      description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
      metrics: 'Complete network visibility',
      color: 'from-orange-500 to-red-600'
    },
    {
      icon: CheckCircle,
      title: 'Continuous Security Assessment',
      description: 'Automated vulnerability scanning, penetration testing, and continuous compliance validation',
      metrics: 'Proactive security validation',
      color: 'from-cyan-500 to-blue-600'
    }
  ]
};
